/**
 * Full-screen flashing popup for new orders vs new contact messages.
 * Distinct colors/icons so Tammy can tell them apart at a glance.
 * Copy rotates — bakery dad-joke energy, still actionable.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { HiBell, HiInbox, HiShoppingCart, HiX } from 'react-icons/hi';

export type AdminFlashAlertKind = 'order' | 'message';

export interface AdminFlashAlertProps {
  kind: AdminFlashAlertKind;
  /** How many new items (shown when > 1). */
  count?: number;
  open: boolean;
  onDismiss: () => void;
  /** Optional extra action (e.g. print order). */
  extraAction?: { label: string; onClick: () => void };
}

interface AlertCopyLine {
  title: string;
  subtitle: string;
  ctaLabel: string;
  dismissLabel: string;
}

/** Pick one line at random — exported for tests. */
export function pickAlertCopy(kind: AdminFlashAlertKind): AlertCopyLine {
  const lines = ALERT_COPY[kind];
  return lines[Math.floor(Math.random() * lines.length)];
}

const ALERT_COPY: Record<AdminFlashAlertKind, AlertCopyLine[]> = {
  order: [
    {
      title: 'Cha-ching! Dough incoming!',
      subtitle:
        'Someone just paid real money for carbs. The oven is doing a little happy dance. (The alarm will too, until you dismiss this.)',
      ctaLabel: 'Show me the goodies',
      dismissLabel: 'Shhh, I’m hiding',
    },
    {
      title: 'Fresh order — not a drill!',
      subtitle:
        'A stranger trusted Nanny B’s with their cravings. Peek at Pending before the cupcakes get impatient.',
      ctaLabel: 'Open the treasure chest',
      dismissLabel: 'Let it marinate',
    },
    {
      title: 'The register just squealed!',
      subtitle:
        'Paid checkout detected. Act cool. Then go see who’s hungry — loop sound stops when you tap dismiss.',
      ctaLabel: 'Feed the queue',
      dismissLabel: 'I’ll pretend I’m busy',
    },
    {
      title: 'Order alert: sugar levels rising!',
      subtitle:
        'New customer order landed. Your pending list grew a little taller. Go say hi before it gets shy.',
      ctaLabel: 'Inspect the damage',
      dismissLabel: 'Not now, flour on hands',
    },
  ],
  message: [
    {
      title: 'Inbox says hello!',
      subtitle:
        'A human typed words at us. Could be love. Could be “where’s my cake?” Either way, Messages is waiting like an eager puppy.',
      ctaLabel: 'Read the tea leaves',
      dismissLabel: 'I’ll caffeinate first',
    },
    {
      title: 'Ping! Not an order — just vibes.',
      subtitle:
        'Contact form alert. Someone wants Tammy’s wisdom (or a refund speech). Chime loops until you shoo this away.',
      ctaLabel: 'Face the inbox',
      dismissLabel: 'Procrastibaking time',
    },
    {
      title: 'New message — drama or delight?',
      subtitle:
        'Could be a compliment. Could be a typo-filled epic. Only one way to find out — and no, ignoring it doesn’t make it vanish.',
      ctaLabel: 'Open Messages',
      dismissLabel: 'Sweetypie can wait',
    },
    {
      title: 'Someone slid into your DMs (politely).',
      subtitle:
        'Website contact message just arrived. Reply when you’re ready — the blue popup will keep flashing until you dismiss it.',
      ctaLabel: 'See what they want',
      dismissLabel: 'Later, buttercup',
    },
  ],
};

const CONFIG = {
  order: {
    Icon: HiShoppingCart,
    ring: 'border-emerald-400 shadow-emerald-500/50',
    panel: 'from-emerald-600 via-green-600 to-emerald-700',
    badge: 'bg-white/20 text-white',
    emoji: '🧁',
    ctaTo: '/admin/orders',
    badgeSingular: 'Hot fresh',
    badgePlural: 'Hot fresh batch',
  },
  message: {
    Icon: HiInbox,
    ring: 'border-sky-400 shadow-sky-500/50',
    panel: 'from-blue-600 via-indigo-600 to-blue-700',
    badge: 'bg-white/20 text-white',
    emoji: '📩',
    ctaTo: '/admin/messages',
    badgeSingular: 'New whisper',
    badgePlural: 'New whispers',
  },
} as const;

export default function AdminFlashAlert({
  kind,
  count = 1,
  open,
  onDismiss,
  extraAction,
}: AdminFlashAlertProps) {
  const [copy, setCopy] = useState<AlertCopyLine>(() => pickAlertCopy(kind));

  // New random quip each time a popup opens (or kind changes while open).
  useEffect(() => {
    if (open) setCopy(pickAlertCopy(kind));
  }, [open, kind]);

  if (!open) return null;

  const cfg = CONFIG[kind];
  const Icon = cfg.Icon;
  const countLabel =
    count > 1
      ? `${count} ${cfg.badgePlural}`
      : cfg.badgeSingular;

  return (
    <div
      className={`fixed inset-0 z-[200] flex justify-center p-4 pointer-events-none ${
        kind === 'message' ? 'items-start pt-[22vh]' : 'items-center'
      }`}
      role="alertdialog"
      aria-live="assertive"
      aria-label={copy.title}
    >
      {/* Dim backdrop — still clickable to dismiss */}
      <button
        type="button"
        className="absolute inset-0 bg-black/40 pointer-events-auto animate-pulse"
        aria-label={copy.dismissLabel}
        onClick={onDismiss}
      />

      <div
        className={`relative pointer-events-auto w-full max-w-md rounded-2xl border-4 ${cfg.ring} shadow-2xl animate-pulse`}
      >
        <div className={`bg-gradient-to-br ${cfg.panel} rounded-xl p-6 text-white`}>
          <div className="flex items-start justify-between gap-3 mb-4">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-full bg-white/20 animate-bounce">
                <Icon className="w-10 h-10" aria-hidden />
              </div>
              <div>
                <span className={`inline-block text-xs font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${cfg.badge} mb-1`}>
                  {countLabel}
                </span>
                <h2 className="text-2xl font-bold flex items-center gap-2">
                  <span aria-hidden>{cfg.emoji}</span>
                  {copy.title}
                </h2>
              </div>
            </div>
            <button
              type="button"
              onClick={onDismiss}
              className="p-2 rounded-lg hover:bg-white/20 transition-colors shrink-0"
              aria-label="Close"
            >
              <HiX className="w-6 h-6" />
            </button>
          </div>

          <p className="text-white/90 text-sm mb-5 flex items-start gap-2">
            <HiBell className="w-5 h-5 shrink-0 mt-0.5 animate-pulse" aria-hidden />
            {copy.subtitle}
          </p>

          <div className="flex flex-wrap gap-2">
            <Link
              to={cfg.ctaTo}
              onClick={onDismiss}
              className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg bg-white font-semibold text-gray-900 hover:bg-gray-100 transition-colors"
            >
              <Icon className="w-5 h-5" />
              {copy.ctaLabel}
            </Link>
            {extraAction && (
              <button
                type="button"
                onClick={() => {
                  extraAction.onClick();
                  onDismiss();
                }}
                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-white/15 border border-white/40 font-medium hover:bg-white/25 transition-colors"
              >
                {extraAction.label}
              </button>
            )}
            <button
              type="button"
              onClick={onDismiss}
              className="inline-flex items-center px-4 py-2.5 rounded-lg text-white/90 hover:bg-white/10 font-medium"
            >
              {copy.dismissLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
